import { Badge, Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react'
import { Building2, Sparkles } from 'lucide-react'
import React from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { BotResponse } from '../../types/chatTypes'

// Utility function to get the beginning of the answer text
const getAnswerPreview = (text: string, maxLength = 150): string => {
  if (!text) return ''

  // Step 1: Replace technical/markdown elements with user-friendly indicators
  const cleanText = text
    // Replace code blocks with a more friendly indicator
    .replace(/```[\s\S]*?```/g, '📝 [Code snippet]')
    // Replace inline code with simplified version
    .replace(/`([^`]+)`/g, '$1')
    // Replace images with descriptive text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, (_, alt) =>
      alt ? `🖼️ [${alt}]` : '🖼️ [Image]'
    )
    // Replace links with just their text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Replace bullet points with a cleaner format
    .replace(/^[*-]\s+/gm, '• ')
    // Normalize whitespace while preserving intentional line breaks
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim()

  // Step 2: Smart truncation that respects sentence boundaries
  if (cleanText.length <= maxLength) return cleanText

  // Try to find a sentence boundary near the maxLength
  const sentenceBoundary = cleanText
    .slice(0, maxLength)
    .match(/.*[.!?](?=\s|$)/)

  if (sentenceBoundary) {
    // If we found a sentence boundary, use that
    return sentenceBoundary[0].trim()
  }

  // If no sentence boundary, try to break at a word boundary
  const wordBoundary = cleanText
    .slice(0, maxLength)
    .match(/.*\b/)

  if (wordBoundary) {
    return wordBoundary[0].trim() + '…' // Using ellipsis character
  }

  // Fallback to simple truncation
  return cleanText.slice(0, maxLength).trim() + '…'
}

interface SecondaryAnswerToggleProps {
  answer: BotResponse
  isExpanded: boolean
  onToggle: () => void
  onPromoteToPrimary?: () => void
  isLoading?: boolean
}

export const SecondaryAnswerToggle: React.FC<SecondaryAnswerToggleProps> = ({
  answer,
  isExpanded,
  onToggle,
  onPromoteToPrimary,
  isLoading
}) => {
  const { t } = useLanguage()

  // Color mode values
  const previewBg = useColorModeValue('white', 'gray.700')
  const previewHoverBg = useColorModeValue('gray.50', 'gray.600')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.700', 'gray.200')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')
  const linkColor = useColorModeValue('blue.600', 'blue.300')

  const getSourceInfo = () => {
    const isInternal = answer.knowledgeSource === 'INTERNAL_KB'
    return {
      label: isInternal ? t('common.internalGuidelines') : t('common.generalInformation'),
      icon: isInternal ? Building2 : Sparkles,
      colorScheme: isInternal ? 'blue' : 'gray',
      bgColor: isInternal ? 'blue.50' : 'gray.50',
      borderColor: isInternal ? 'blue.200' : 'gray.200',
      hoverBg: isInternal ? 'blue.100' : 'gray.100',
      hoverBorder: isInternal ? 'blue.300' : 'gray.300'
    }
  }

  const sourceInfo = getSourceInfo()
  const IconComponent = sourceInfo.icon
  const answerPreview = answer.answer ? getAnswerPreview(answer.answer) : ''

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }

  if (isExpanded) {
    // When expanded, just show the content area with action buttons
    return (
      <Box>
        {/* Action buttons when expanded */}
        {onPromoteToPrimary && (
          <Box px={3} pb={3}>
            <Flex justify="flex-end">
              <Button
                size="xs"
                variant="outline"
                colorScheme={sourceInfo.colorScheme}
                onClick={onPromoteToPrimary}
                aria-label={`${t('common.promoteSecondary')} - ${sourceInfo.label}`}
                leftIcon={<IconComponent size={12} />}
              >
                {t('common.promoteSecondary')}
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    )
  }

  // When minimized, show a subtle expandable preview
  return (
    <Box
      as="button"
      w="100%"
      position="relative"
      p={0}
      cursor="pointer"
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      textAlign="left"
      _hover={{
        '& > .preview-content': {
          bg: previewHoverBg,
        },
        '& .expand-indicator': {
          transform: 'translateX(4px)',
        }
      }}
      _focus={{
        outline: 'none',
        '& > .preview-content': {
          bg: previewHoverBg,
        }
      }}
      _focusVisible={{
        '& > .preview-content': {
          outline: '2px solid',
          outlineColor: 'blue.400',
          outlineOffset: '-1px'
        }
      }}
      transition="all 0.2s"
      aria-expanded={false}
      aria-controls={`secondary-answer-${answer.message_id}`}
      aria-label={`${t('common.expand')} ${sourceInfo.label}`}
    >
      {/* Loading indicator */}
      {isLoading && (
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="gray"
          variant="outline"
          fontSize="xs"
          zIndex={1}
        >
          {answer.knowledgeSource === 'INTERNAL_KB'
            ? t('common.progressInternal')
            : t('common.progressExternal')
          }
        </Badge>
      )}

      {/* Preview content with subtle gradient */}
      <Box
        className="preview-content"
        w="100%"
        borderBottom="1px solid"
        borderColor={borderColor}
        bg={previewBg}
        pt={2}
        pb={3}
        px={4}
        position="relative"
        transition="all 0.2s"
        role="button"
        _hover={{
          bg: previewHoverBg,
        }}
      >
        {answerPreview ? (
          <>
            <Text
              fontSize="sm"
              color={textColor}
              lineHeight="1.5"
              noOfLines={2}
              mb={2}
            >
              {answerPreview}
            </Text>

            {/* Clearer but still subtle expand indicator */}
            <Flex
              className="expand-indicator"
              justify="flex-start"
              align="center"
              gap={1.5}
              opacity={0.8}
              transition="all 0.2s"
            >
              <Text
                fontSize="xs"
                color={linkColor}
                fontWeight="medium"
              >
                {t('common.showMore')}
              </Text>
              <Box
                as="span"
                fontSize="xs"
                color={linkColor}
                transform="rotate(90deg)"
                display="inline-block"
              >
                →
              </Box>
            </Flex>
          </>
        ) : !isLoading && (
          <Text
            fontSize="sm"
            color={mutedTextColor}
            fontStyle="italic"
            textAlign="center"
          >
            {answer.knowledgeSource === 'INTERNAL_KB'
              ? t('common.noInternalKnowledge')
              : t('common.noAdditionalResponse')
            }
          </Text>
        )}
      </Box>
    </Box>
  )
}
